<!-- 
화면명 : ShipmentList.vue
작성일 : 2025-07-01
설명   : 출고일보 화면
-->
<template>
  <div class="relative bg-blueGray-100">
    <div class="flex justify-between mt-2">
      <div class="flex text-black font-bold text-lg items-center justify-center">
        <img src="@/assets/img/breadcrumbs.svg" class="w-4 h-4 mx-2" />
        출고일보
      </div>
      <div class="flex mx-2">
        <UCButton label="조회" width="80px" icon="search" @click="btnSearchClick" />
        <UCButton label="엑셀" width="80px" icon="excel" @click="btnExcelClick" />
        <UCButton label="출력" width="80px" icon="printer" @click="btnReportClick"/>
      </div>
    </div>

    <div class="bg-white p-1 rounded shadow mt-2 top-wrapper">
      <div
        class="w-full overflow-x-visible inline-flex flex-wrap items-center gap-6 whitespace-nowrap px-4 py-2 border rounded bg-white shadow-sm"
      >
        <div class="flex space-x-2">
          <UCDate label="발주일자" v-model="dtStart" />
          <span class="pt-2">&nbsp;~</span>
          <UCDate label="" v-model="dtEnd" />
        </div>

        <UCRadio
          label="작업구분"
          v-model="rdoGubun.cd"
          :arrData="rdoGubun.arr"
          code="value"
          name="label"
        />

        <div class="flex items-center space-x-2">
          <UCVendor
            title="거래선"
            :VendCd="userStore.userVendCd"
            :VendName="userStore.userName"
          />
        </div>
      </div>
    </div>

    <!-- 소계 구분 -->
    <div class="mt-2 flex justify-between items-center">
      <label class="flex text-blueGray-600 text-base font-semibold whitespace-nowrap items-center justify-center" >
          <img src="@/assets/img/icon-list.svg" class="flex w-5 h-5 mr-2" />
        출고리스트
      </label>

      <UCRadio
        label="소계구분"
        v-model="rdoSubTotalGubun.cd"
        :arrData="rdoSubTotalGubun.arr"
        code="value"
        name="label"
      />
    </div>

    <!-- 그리드 -->
    <div class="mt-2 mb-2">
      <UCGrid
        :data="arrSearchData"
        :isRowNumber="true"
        :columns="columns"
        :options="gridOptions"
        :selectable="true"
        :pagination="true"
        :paginationSize="50"
        autoHeight
        :excludeSelectors="['.top-wrapper']"
        :heightOffset="30"
        @row-selected="handleSelection"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { format as formatDate } from "date-fns";
import { notify } from "@kyvg/vue3-notification";

import { useUserStore } from "@/store/userStore";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

import UCVendor from "@/components/UserControl/UCVendor.vue";
import UCGrid from "@/components/UserControl/UCGrid.vue";
import UCText from "@/components/UserControl/UCText.vue";
import UCCombo from "@/components/UserControl/UCCombo.vue";
import UCDate from "@/components/UserControl/UCDate.vue";
import UCRadio from "@/components/UserControl/UCRadio.vue";
import UCButton from "@/components/UserControl/UCButton.vue";

import { apiExecuteDataSet ,apiExecuteReport} from "@/api/SCM/common";

const userStore = useUserStore();

onMounted(() => {
  // console.log("mounted");

  bindCombo();

  btnSearchClick();
});

// 처리 구분
const rdoGubun = ref({
  cd: "1",
  arr: [
    { label: "자재", value: "1" },
    { label: "외주", value: "2" },
  ],
});

const dtStart = ref("");
const dtEnd = ref("");

// 소계 구분
const rdoSubTotalGubun = ref({
  cd: "TOTAL",
  arr: [
    { label: "전체", value: "TOTAL" },
    { label: "발주일자별", value: "ORDDT" },
    { label: "입고일자별", value: "IPGDT" },
  ],
});

async function bindCombo() {
  // console.log("bindCombo");

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  const today2 = new Date(year, month, 1);
  dtStart.value = formatDate(today2, "yyyy-MM-dd");
  dtEnd.value = formatDate(today, "yyyy-MM-dd");
}

async function btnSearchClick() {
  // console.log("btnSearchClick", rdoSubTotalGubun.value.cd);

  // SP_CN_MT06IPGR_SELECTLIST_OCM
  const searchParams = {
    
    ID: "WERP\\T_MT06IPGR\\R112",
    Parameter: {
      "@FM05ORDDATE": dtStart.value.replaceAll("-", ""),
      "@TM05ORDDATE": dtEnd.value.replaceAll("-", ""),
      "@GUBN": rdoGubun.value.cd,
      "@M05CUSNO": userStore.userVendCd,
      //"@M05CUSNA": "㈜중산기업",
      "@SUBTOTALGBN": rdoSubTotalGubun.value.cd,
      "@PAGENO": 1,
      "@PAGEINROW": 10000,
      "@SIDX": null,
      "@SORD": null,
      "@CURPAGE": null,
      "@MAXPAGE": null,
      "@TOTALCOUNT": null,
    },
  };

  const res = await apiExecuteDataSet(searchParams);
  // console.log("res", res._Result);

  arrSearchData.value = res._Result[0];
}

const arrSearchData = ref([]);
const selectedRows = ref([]);
function handleSelection(data) {
  selectedRows.value = data;
  console.log("선택된 행:", data);
}

const gridOptions = {
  selectableCheck: function (row) {
    const data = row.getData();
    const matCd = data?.M05MATCD ? String(data.M05MATCD).trim() : "";
    return !matCd.includes("소계") && !matCd.includes("총계");
  },
  rowFormatter: function (row) {
    const data = row.getData();
    const matCd = data?.M05MATCD ? String(data.M05MATCD).trim() : "";
    const element = row.getElement();

    // 자재코드가 '소계' 또는 '총계'를 포함하는 경우
    if (matCd.includes("소계") || matCd.includes("총계")) {
      element.style.backgroundColor = "#fef9c3"; // 강조 배경색 (연한 옐로우/앰버 톤)
      element.style.fontWeight = "bold";
      element.style.color = "#0f172a";

      const chk = element.querySelector("input[type='checkbox']");
      if (chk) {
        chk.style.display = "none";
      }
    } else {
      element.style.backgroundColor = "";
      element.style.fontWeight = "";
      element.style.color = "";

      const chk = element.querySelector("input[type='checkbox']");
      if (chk) {
        chk.style.display = "";
      }
    }
  },
};

const columns = [
  { title: "자재코드", field: "M05MATCD", width: 120, hozAlign: "center" },
  { title: "자재명", field: "M05MATNA", width: 200, hozAlign: "left" },
  { title: "규격", field: "M05SIZE", width: 120, hozAlign: "left" },
  { title: "재질", field: "M05MATL", width: 120, hozAlign: "left" },
  { title: "단위", field: "M05UNIT", width: 70, hozAlign: "center" },
  {
    title: "발주정보", // 상위 그룹
    columns: [
      { title: "발주번호", field: "M05ORDKEY", width: 120, hozAlign: "center" },
      { title: "발주구분", field: "M05ORDGU", width: 120, hozAlign: "center" },
      {
        title: "발주량",
        field: "M05ORDQTY",
        width: 100,
        hozAlign: "right",
        formatter: "money",
        formatterParams: {
          thousand: ",",
          precision: 0,
        },
      },
    ],
  },
  {
    title: "입고정보", // 상위 그룹
    columns: [
      {
        title: "입고일자",
        field: "M06MOADATE",
        width: 120,
        hozAlign: "center",
      },
      { title: "입고번호", field: "M06IPGKEY", width: 120, hozAlign: "center" },
      { title: "입고구분", field: "M06IPGGU", width: 120, hozAlign: "center" },
      {
        title: "입고량",
        field: "M05IPGQTY",
        width: 100,
        hozAlign: "right",
        formatter: "money",
        formatterParams: {
          thousand: ",",
          decimal: ".",
          precision: 0, // 소수점 자릿수 (예: 2면 100.00)
        },
      },
    ],
  },
  {
    title: "금액",
    field: "M06AMOUNT",
    width: 120,
    hozAlign: "right",
    formatter: "money",
    formatterParams: {
      thousand: ",",
      decimal: ".",
      precision: 0, // 소수점 자릿수 (예: 2면 100.00)
    },
  },
];

async function btnExcelClick() {
  // console.log("btnExcelClick");

  // 조회랑 데이터가 달라서 다시 조회
  const searchParams = {
    
    ID: "WERP\\T_MT06IPGR\\R113",
    Parameter: {
      "@CUSNO": userStore.userVendCd,
      "@F_CD": dtStart.value.replaceAll("-", ""),
      "@T_CD": dtEnd.value.replaceAll("-", ""),
      "@WRKGBN": rdoGubun.value.cd,
      "@SGBN": "2",
    },
  };

  const res = await apiExecuteDataSet(searchParams);
  // console.log("res", res._Result);

  if (res._Result[0].length === 0) {
    notify({
      title: "실패",
      text: "조회된 데이터 건수가 없습니다.",
      type: "error",
    });
    return;
  }

  const templatePath = "/excel/출고리스트(자재).xlsx";
  const today = new Date();

  try {
    // 템플릿 파일 가져오기
    const response = await fetch(templatePath);
    const buffer = await response.arrayBuffer();

    // 워크북 로드
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    // 첫 번째 워크시트 선택
    const worksheet = workbook.getWorksheet(1);

    // 데이터 바인딩
    worksheet.getCell("A2").value = `발주일자 : ${
      dtStart.value.replaceAll("-", "").slice(0, 4) +
      "-" +
      dtStart.value.replaceAll("-", "").slice(4, 6) +
      "-" +
      dtStart.value.replaceAll("-", "").slice(6, 8)
    } ~ ${
      dtEnd.value.replaceAll("-", "").slice(0, 4) +
      "-" +
      dtEnd.value.replaceAll("-", "").slice(4, 6) +
      "-" +
      dtEnd.value.replaceAll("-", "").slice(6, 8)
    }`;
    worksheet.getCell("A3").value = `거래선 : ${
      userStore.userVendCd.substring(0, 3) +
      "-" +
      userStore.userVendCd.substring(4, 2) +
      "-" +
      userStore.userVendCd.substring(6)
    }`;

    worksheet.getCell("X2").value = `작성자 : ${userStore.userName}`;
    worksheet.getCell("X3").value = `출력일자 :  ${formatDate(
      today,
      "yyyy-MM-dd"
    )}`;

    // 테이블 데이터 바인딩
    const startRow = 6; // 데이터가 시작될 행 번호 (예: 6번째 행부터 시작)
    res._Result[0].forEach((item, index) => {
      // console.log("forEach", index, item);

      const row = worksheet.getRow(startRow + index);
      row.getCell(1).value = item.매입선;
      row.getCell(2).value = item.매입선명;
      row.getCell(3).value = item.시작입고일;
      row.getCell(4).value = item.종료입고일;
      row.getCell(5).value = item.입고번호;
      row.getCell(6).value = item.입고구분;
      row.getCell(7).value = item.지불구분;
      row.getCell(8).value = item.일련번호;
      row.getCell(9).value = item.자재코드;
      row.getCell(10).value = item.자재코드명;
      row.getCell(11).value = item.규격;
      row.getCell(12).value = item.재질;
      row.getCell(13).value = item.도면번호;
      row.getCell(14).value = item.발주번호;
      row.getCell(15).value = item.단위;
      row.getCell(16).value = item.발주량;
      row.getCell(17).value = item.입고량;
      row.getCell(18).value = item.단가;
      row.getCell(19).value = item.입고금액;
      row.getCell(20).value = item.납기일자;
      row.getCell(21).value = item.공사번호;
      row.getCell(22).value = item.근거번호;
      row.getCell(23).value = item.구분;
      row.getCell(24).value = item.납품처;
    });

    // 합계 데이터 바인딩
    // worksheet.getCell('D' + (startRow + dataToBind.salesData.length)).value = dataToBind.totalSales;

    // 변경된 워크북을 버퍼로 변환
    const excelBuffer = await workbook.xlsx.writeBuffer();

    // 파일을 Blob으로 만들고 다운로드
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `출고리스트(자재)_${formatDate(today, "yyyyMMdd")}.xlsx`);

    notify({
      title: "성공",
      text: "엑셀 다운로드가 완료되었습니다.",
      type: "success",
    });
  } catch (error) {
    // console.error("엑셀 다운로드 중 오류 발생:", error);

    notify({
      title: "실패",
      text: "엑셀 다운로드에 실패했습니다." + error,
      type: "error",
    });
  }
}



//출고일보
async function btnReportClick() {
  const searchParams = {
    
    ID: "W000\\W000\\R-OCM\\WY02PSR",
    Parameter: {
      "@CUSNO": userStore.userVendCd,
      "@F_CD": dtStart.value.replaceAll("-", ""),
      "@T_CD": dtEnd.value.replaceAll("-", ""),
      "@WRKGBN": rdoGubun.value.cd,
      "@SGBN": '2'
    },
  };

  const exportParams = {
    title: "출고일보",
    //width: 1100,
    //height: 900,
  };

  await apiExecuteReport(searchParams, exportParams);
}

</script>
